/**
 * Builds the hierarchical page tree from the flat list
 * synchronized by PowerSync (one subscription → in-memory tree).
 */
import { createMemo } from "solid-js";
import type { Page } from "@apunte-digital/powersync";
import { usePagesQuery } from "@apunte-digital/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

export interface PageTreeNode {
  page: Page;
  children: PageTreeNode[];
}

/**
 * Flattens the tree in depth-first order (preorder), which is the same visual order
 * as the sidebar. It is the broad-sense sibling list that the detail carousel
 * traverses: swiping advances/retreats to the adjacent page as they appear.
 */
export function flattenPageTree(nodes: PageTreeNode[]): Page[] {
  const flat: Page[] = [];
  const walk = (list: PageTreeNode[]) => {
    for (const node of list) {
      flat.push(node.page);
      walk(node.children);
    }
  };
  walk(nodes);
  return flat;
}

function buildPageTree(pages: Page[]): PageTreeNode[] {
  const nodesById = new Map<string, PageTreeNode>();
  for (const page of pages) {
    nodesById.set(page.id, { page, children: [] });
  }

  const roots: PageTreeNode[] = [];
  for (const node of nodesById.values()) {
    const parentId = node.page.parent_page_id;
    const parent = parentId ? nodesById.get(parentId) : undefined;
    if (parent && parentId !== node.page.id) {
      parent.children.push(node);
    } else {
      // No parent, or parent not visible (e.g. only the subpage was shared)
      roots.push(node);
    }
  }
  return roots;
}

export function usePageTree() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const pages = usePagesQuery({
    db: () => db(),
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const tree = createMemo(() => buildPageTree(pages.data ?? []));

  return { pages, tree };
}
